import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Send, Home, Briefcase, Sparkles, MapPin, Route, CreditCard } from 'lucide-react';

const ChatPanel = ({ expanded, onToggle, messages, onSendMessage, onViewChange, userLocations, isLoading, onCheckout, checkoutProduct }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSendMessage(input);
    setInput('');
  };

  // Dynamic quick actions based on user locations
  const quickActions = useMemo(() => {
    const actions = [
      {
        icon: <Sparkles size={14} />,
        label: 'Tańsze karnety',
        action: () => onSendMessage('Pokaż najtańsze karnety do 100 zł')
      }
    ];

    // Add location-based actions if user has locations set
    if (userLocations?.home && userLocations?.work) {
      actions.unshift({
        icon: <Route size={14} />,
        label: 'Dla mojej trasy',
        action: () => onSendMessage('Jaki karnet będzie najlepszy dla mojej trasy dom-praca?')
      });
    } else if (userLocations?.home) {
      actions.unshift({
        icon: <Home size={14} />,
        label: 'Blisko domu',
        action: () => onSendMessage('Pokaż siłownie blisko mojego domu')
      });
    } else if (userLocations?.work) {
      actions.unshift({
        icon: <Briefcase size={14} />,
        label: 'Blisko pracy',
        action: () => onSendMessage('Pokaż siłownie blisko mojej pracy')
      });
    }

    // Add premium option
    actions.push({
      icon: <Sparkles size={14} />,
      label: 'Premium',
      action: () => onSendMessage('Pokaż karnety premium z basenem i spa')
    });

    // If no locations set, add hint to set them
    if (!userLocations?.home && !userLocations?.work) {
      actions.push({
        icon: <MapPin size={14} />,
        label: 'Ustaw lokalizację',
        action: () => onSendMessage('Chcę ustawić moją lokalizację żebyś mógł polecić mi karnety')
      });
    }

    return actions;
  }, [userLocations, onSendMessage]);

  return (
    <div className="chat-panel-wrapper">
      {/* Chat Header */}
      <div className="chat-header">
        <MessageCircle size={20} />
        <span>Asystent AI</span>
      </div>

      {/* Messages Area */}
      <div className="messages-area">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <div className="welcome-icon">
              <Sparkles size={32} />
            </div>
            <h3>Witaj w FitPass!</h3>
            <p>Jestem Twoim asystentem AI. Pomogę Ci znaleźć idealny karnet sportowy.</p>
            <p className="hint">Zapytaj mnie o karnety, filtry, lub kliknij przyciski poniżej.</p>
          </div>
        ) : (
          <div className="messages-scroll">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  className={`message ${msg.role}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="message-content">
                    {msg.content}
                  </div>
                  {/* Show checkout button after checkout message */}
                  {msg.role === 'assistant' && msg.content.includes('Przygotowuję proces płatności') && checkoutProduct && (
                    <motion.button
                      className="checkout-btn"
                      onClick={() => onCheckout(checkoutProduct)}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <CreditCard size={18} />
                      <span>Przejdź do płatności - ${checkoutProduct.price}</span>
                    </motion.button>
                  )}
                </motion.div>
              ))}

              {isLoading && (
                <motion.div
                  className="message assistant"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="message-content typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>
        )}
      </div>

      {/* Input Bar */}
      <div className="chat-input-bar">
        {/* Quick action chips */}
        <div className="quick-chips">
          {quickActions.map((action, index) => (
            <motion.button
              key={index}
              className="quick-chip"
              onClick={action.action}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {action.icon}
              <span>{action.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Input row */}
        <div className="input-row">
          <div className="ai-indicator">
            <MessageCircle size={20} />
          </div>

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Zapytaj o karnety, filtruj produkty..."
            className="chat-input"
          />

          <motion.button
            className="send-btn"
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Send size={18} />
          </motion.button>
        </div>
      </div>

      <style jsx>{`
        .chat-panel-wrapper {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: transparent;
        }

        .chat-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-weight: 600;
          font-size: 0.9375rem;
          flex-shrink: 0;
        }

        .messages-area {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .welcome-message {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
          text-align: center;
          color: #4b5563;
        }

        .welcome-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 1rem;
        }

        .welcome-message h3 {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .welcome-message p {
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .welcome-message .hint {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .messages-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .message {
          display: flex;
          max-width: 85%;
        }

        .message.user {
          align-self: flex-end;
        }

        .message.assistant {
          align-self: flex-start;
        }

        .message-content {
          padding: 0.75rem 1rem;
          border-radius: 12px;
          font-size: 0.875rem;
          line-height: 1.5;
          white-space: pre-wrap;
        }

        .message.user .message-content {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .message.assistant .message-content {
          background: #f3f4f6;
          color: #1f2937;
        }

        .checkout-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
          margin-top: 0.75rem;
          padding: 1rem 1.5rem;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          transition: all 0.2s;
        }

        .checkout-btn:hover {
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
        }

        .message-content.typing {
          display: flex;
          gap: 0.4rem;
          padding: 1rem;
        }

        .message-content.typing span {
          width: 8px;
          height: 8px;
          background: #9ca3af;
          border-radius: 50%;
          animation: typing 1.4s infinite;
        }

        .message-content.typing span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .message-content.typing span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.5;
          }
          30% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }

        .chat-input-bar {
          background: #f9fafb;
          padding: 0.75rem 1rem 1rem;
          border-top: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          flex-shrink: 0;
        }

        .quick-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          padding-bottom: 0.25rem;
        }

        .quick-chip {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 0.75rem;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #4b5563;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .quick-chip:hover {
          background: #667eea;
          color: white;
          border-color: #667eea;
        }

        .input-row {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .ai-indicator {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .chat-input {
          flex: 1;
          padding: 0.875rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 0.9375rem;
          transition: all 0.2s;
        }

        .chat-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .send-btn {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.2s;
        }

        .send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ChatPanel;
